import { FC, useCallback } from 'react'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'
import {
  CoverLetterDetail_CurrentUserFragment,
  useViewerCheckedParagraphScrapGuideMutation,
} from 'generated/graphql'
import { mobileBreakPoint } from 'app.styled/media'
import CloseOutlined from 'svgIcons/CloseOutlined'

const NEVER_SEEING_PARAGRAPH_SCRAP_GUIDE_AGAINE_COOKIE_NAME =
  'naver_seeing_paragraph_scrap_guide_again_cookie'

interface SelectionGuideBarProps {
  currentUser?: CoverLetterDetail_CurrentUserFragment
}

const SelectionGuideBar: FC<SelectionGuideBarProps> = ({ currentUser }) => {
  const [cookies, setCookies] = useCookies()

  const [viewerCheckedParagraphScrapGuide] =
    useViewerCheckedParagraphScrapGuideMutation()

  let openParagraphScrapGuide = !currentUser?.isCheckedParagraphScrapGuide

  const hasNeverSeeingParagraphScrapGuideAgainCookie =
    !!cookies?.[NEVER_SEEING_PARAGRAPH_SCRAP_GUIDE_AGAINE_COOKIE_NAME]

  if (hasNeverSeeingParagraphScrapGuideAgainCookie) {
    openParagraphScrapGuide = false
  }

  const handleCloseClick = useCallback(() => {
    setCookies(NEVER_SEEING_PARAGRAPH_SCRAP_GUIDE_AGAINE_COOKIE_NAME, true)
  }, [])

  const handlePermanantCloseClick = useCallback(async () => {
    if (!currentUser) {
      setCookies(NEVER_SEEING_PARAGRAPH_SCRAP_GUIDE_AGAINE_COOKIE_NAME, true)
      return
    }

    await viewerCheckedParagraphScrapGuide()
  }, [])

  return (
    <>
      {openParagraphScrapGuide && (
        <StyledRoot>
          <StyledMainWrap>
            <StyledMain>
              <StyledTitle>
                마음에 드는 문장을 스크랩 할 수 있어요!
                <HiddenMdUp>지금 바로 PC에서 이용해보세요.</HiddenMdUp>
              </StyledTitle>
              <HiddenSmDown>
                <StyledExample>
                  최고 품질의 상품들을{' '}
                  <StyledHighlight>
                    지금보다 더 많은 소비자들이 여러 유통 채널에서 더욱 폭 넓고
                    쉽게
                  </StyledHighlight>
                  ...
                </StyledExample>
              </HiddenSmDown>
            </StyledMain>
            <HiddenSmDown>
              <StyledIconButton onClick={handleCloseClick}>
                <StyledCloseIcon />
              </StyledIconButton>
            </HiddenSmDown>
            <HiddenMdUp>
              <StyledIconButton onClick={handlePermanantCloseClick}>
                <StyledCloseIcon />
              </StyledIconButton>
            </HiddenMdUp>
          </StyledMainWrap>
          <HiddenSmDown>
            <StyledFooter>
              <StyledHideBtn onClick={handlePermanantCloseClick}>
                다시보지 않기
              </StyledHideBtn>
            </StyledFooter>
          </HiddenSmDown>
        </StyledRoot>
      )}
    </>
  )
}

export default SelectionGuideBar

const tabletBreakPoint = '@media (min-width: 960px)'

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #eee;
  padding: 15px 20px;

  ${mobileBreakPoint} {
    padding: 21px 20px;
  }
`

const StyledMainWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;

  ${mobileBreakPoint} {
    justify-content: space-around;
  }
`

const StyledMain = styled.div`
  color: #333;
  margin-top: 15px;

  ${mobileBreakPoint} {
    margin-top: 0;
    margin-right: 15px;
  }
`

const StyledTitle = styled.div`
  font-weight: bold;
  line-height: 1.38;
  font-size: 16px;
`

const StyledExample = styled.p`
  line-height: 1.47;
  font-size: 15px;
  padding-top: 10px;
`

const StyledHighlight = styled.span`
  color: inherit;
  background-color: #ffcccc;
`

const StyledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  background-color: transparent;
  outline: 0;
  border: 0;
  margin: 0;
  border-radius: 0;
  padding: 0;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  text-decoration: none;
  color: inherit;
  border-radius: 50%;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    pointer-events: none;
    cursor: default;
  }
`

const StyledCloseIcon = styled(CloseOutlined)`
  color: #333;
  width: 21px;
  height: 21px;
  font-size: 25px;

  path {
    fill: #333;
  }
`

const StyledFooter = styled.footer`
  text-align: right;
  padding-top: 11px;
`

const StyledHideBtn = styled.button`
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: #999;
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
`

const HiddenSmDown = styled.div`
  ${mobileBreakPoint} {
    display: none;
  }
`

const HiddenMdUp = styled.div`
  ${tabletBreakPoint} {
    display: none;
  }
`
